import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Calendar, Home, Check, Printer, Info, MapPin, Mail, Download } from 'lucide-react';
import Layout from '../Layout';
import { WorkshopManager } from '../../helpers/workshop';
import { showToast } from '../../helpers/customToast';
import { verifyPaymentStatus } from '../../helpers/stripe';

const WorkshopConfirmationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const verificationAttempted = useRef(false);

  useEffect(() => {
    const verifyPayment = async () => {
      // Skip if verification was already attempted
      if (verificationAttempted.current) {
        return;
      }
      verificationAttempted.current = true;

      try {
        setLoading(true);
        const searchParams = new URLSearchParams(location.search);
        const documentId = searchParams.get('document_id');
        const sessionId = searchParams.get('session_id');
        const isFree = searchParams.get('free') === 'true';

        if (!documentId) {
          throw new Error('Paramètres manquants');
        }

        // Handle free workshops (no payment required)
        if (isFree) {
          const reservationData = await WorkshopManager.getReservationByDocumentId(documentId);
          setReservation(reservationData);
          setLoading(false);
          return;
        }

        // Handle paid workshops - require session_id
        if (!sessionId) {
          // Check if we have it in location state
          if (location.state?.reservationId) {
            const reservationData = await WorkshopManager.getReservationByDocumentId(location.state.documentId);
            setReservation(reservationData);
            setLoading(false);
            return;
          }

          throw new Error('Paramètres manquants');
        }

        // Verify payment status for paid workshops
        const paymentStatus = await verifyPaymentStatus(sessionId, documentId);

        if (paymentStatus.success) {
          // Get reservation details
          const reservationData = await WorkshopManager.getReservationByDocumentId(documentId);
          setReservation(reservationData);

          // Update reservation status if necessary
          if (reservationData.resa_status !== 'confirmed') {
            await WorkshopManager.updateReservation(reservationData.id, {
              resa_status: 'confirmed'
            });
          }
        } else {
          throw new Error('Vérification du paiement échouée');
        }
      } catch (error) {
        console.error('Error in payment verification:', error);
        showToast('Une erreur est survenue lors de la vérification de votre paiement', 'error');
        navigate('/ateliers');
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [location, navigate]);

  const formatDate = (dateString) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  const handlePrint = () => {
    window.print();
  };

  const downloadCalendarInvite = () => {
    if (!reservation || !reservation.workshop_date || !reservation.workshop_date[0]) {
      console.error('Missing reservation or workshop_date:', reservation);
      showToast('Impossible de générer l\'invitation. Données manquantes.', 'error');
      return;
    }

    const workshopDate = Array.isArray(reservation.workshop_date)
      ? reservation.workshop_date[0]
      : reservation.workshop_date;

    const date = new Date(workshopDate.date);

    if (isNaN(date.getTime())) {
      console.error('Invalid date:', workshopDate.date, 'Full reservation:', reservation);
      showToast('Date invalide. Impossible de générer l\'invitation.', 'error');
      return;
    }

    const endDate = new Date(date);
    endDate.setMinutes(endDate.getMinutes() + (reservation.workshop?.duration || 90));

    const title = reservation.workshop?.title || 'Atelier OVLM';
    const description = reservation.workshop?.short_description || 'Atelier OVLM';
    const location = reservation.workshop?.location || '';

    // Format dates for iCal
    const formatICalDate = (date) => {
      return date.toISOString().replace(/-|:|\.\d+/g, "");
    };

    const iCalContent = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "CALSCALE:GREGORIAN",
      "PRODID:-//OVLM//Ateliers//FR",
      "METHOD:PUBLISH",
      "BEGIN:VEVENT",
      `SUMMARY:${title}`,
      `DTSTART:${formatICalDate(date)}`,
      `DTEND:${formatICalDate(endDate)}`,
      `LOCATION:${location}`,
      `DESCRIPTION:${description}`,
      "STATUS:CONFIRMED",
      `UID:${reservation.documentId}@ovlm.fr`,
      "SEQUENCE:0",
      `DTSTAMP:${formatICalDate(new Date())}`,
      "END:VEVENT",
      "END:VCALENDAR"
    ].join("\r\n");

    const blob = new Blob([iCalContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `atelier-ovlm-${formatICalDate(date).substring(0, 8)}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Layout>
        <div className="container my-5 text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3">Traitement de votre réservation...</p>
        </div>
      </Layout>
    );
  }

  if (!reservation) {
    return (
      <Layout>
        <div className="container my-5">
          <div className="alert alert-danger">
            Réservation introuvable
          </div>
          <Link to="/ateliers" className="btn btn-primary">
            Retour aux ateliers
          </Link>
        </div>
      </Layout>
    );
  }

  const workshop = reservation.workshop;
  const workshopDate = Array.isArray(reservation.workshop_date)
    ? reservation.workshop_date[0]
    : reservation.workshop_date;

  return (
    <Layout className="workshop-confirmation-page">
      <div className="container my-5 print-content">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Confirmation de réservation</h2>
          <div className="d-print-none">
            <button onClick={handlePrint} className="btn btn-outline-secondary me-2">
              <Printer size={18} className="me-1" />
              Imprimer
            </button>
            <button onClick={downloadCalendarInvite} className="btn btn-outline-primary">
              <Calendar size={18} className="me-1" />
              Ajouter au calendrier
            </button>
          </div>
        </div>

        <div className="row">
          <div className="col-12 mb-4">
            <div className="alert alert-success" role="alert">
              <div className="d-flex">
                <Check size={24} className="me-3" />
                <div>
                  <h4 className="alert-heading">Réservation confirmée !</h4>
                  <p className="mb-0">
                    Votre réservation pour l'atelier <strong>{workshop.title}</strong> a été confirmée.
                    Un email de confirmation a été envoyé à votre adresse email.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-8">
            <div className="card mb-4">
              <div className="card-body">
                <h3 className="card-title">Détails de l'atelier</h3>
                
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Atelier:</strong>
                  </div>
                  <div className="col-sm-8">
                    {workshop.title} <span className="badge bg-primary ms-2">{workshop.reference}</span>
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Date et heure:</strong>
                  </div>
                  <div className="col-sm-8">
                    {formatDate(workshopDate.date)}
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Durée:</strong>
                  </div>
                  <div className="col-sm-8">
                    {workshop.duration} minutes
                  </div>
                </div>
                
                {workshop.location && (
                  <div className="row mb-3">
                    <div className="col-sm-4">
                      <strong>Lieu:</strong>
                    </div>
                    <div className="col-sm-8">
                      {workshop.location}
                    </div>
                  </div>
                )}
                
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Numéro de réservation:</strong>
                  </div>
                  <div className="col-sm-8">
                    #{reservation.id}
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Prix:</strong>
                  </div>
                  <div className="col-sm-8">
                    {reservation.total_price === 0 ? 'Gratuit' : `${reservation.total_price.toFixed(2)}€`}
                  </div>
                </div>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-body">
                <h3 className="card-title">Informations personnelles</h3>
                
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Nom:</strong>
                  </div>
                  <div className="col-sm-8">
                    {reservation.user_details.first_name} {reservation.user_details.last_name}
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Email:</strong>
                  </div>
                  <div className="col-sm-8">
                    {reservation.user_details.email}
                  </div>
                </div>
                
                {reservation.user_details.phone && (
                  <div className="row mb-3">
                    <div className="col-sm-4">
                      <strong>Téléphone:</strong>
                    </div>
                    <div className="col-sm-8">
                      {reservation.user_details.phone}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {workshop.workshop_instructions && (
              <div className="card mb-4">
                <div className="card-body">
                  <h3 className="card-title">Instructions pour l'atelier</h3>
                  <div dangerouslySetInnerHTML={{ __html: workshop.workshop_instructions }} />
                </div>
              </div>
            )}
          </div>

          <div className="col-lg-4">
            <div className="card mb-4">
              <div className="card-body">
                <h3 className="card-title">Rappel</h3>
                <div className="alert alert-info">
                  <Info size={18} className="me-2" />
                  N'oubliez pas de vous présenter 15 minutes avant le début de l'atelier.
                </div>
                
                <h5 className="mt-4">Besoin d'aide ?</h5>
                <p>
                  Pour toute question concernant votre réservation, contactez-nous:
                </p>
                <ul className="list-unstyled">
                  <li className="mb-2">
                    <Mail size={18} className="me-2 text-primary" />
                    <a href="mailto:contact@ovlm97.fr">contact@ovlm97.fr</a>
                  </li>
                  <li className="mb-2">
                    <MapPin size={18} className="me-2 text-primary" />
                    {workshop.location || 'Lieu à confirmer'}
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="card mb-4 d-print-none">
              <div className="card-body">
                <h3 className="card-title">Politique d'annulation</h3>
                {reservation.total_price > 0 ? (
                  <p>
                    Vous pouvez annuler votre réservation jusqu'à 48 heures avant le début de l'atelier et recevoir un remboursement complet.
                    Pour toute annulation effectuée moins de 48 heures avant l'atelier, des frais d'annulation s'appliqueront.
                  </p>
                ) : (
                  <p>
                    Vous pouvez annuler votre réservation jusqu'à 48 heures avant le début de l'atelier sans frais.
                  </p>
                )}
                <p>
                  Pour annuler votre réservation, contactez-nous par email à <a href="mailto:contact@ovlm97.fr">contact@ovlm97.fr</a>
                  en indiquant votre numéro de réservation.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="d-print-none text-center mt-4">
          <Link to="/ateliers" className="btn btn-primary me-3">
            <Calendar size={18} className="me-1" />
            Découvrir d'autres ateliers
          </Link>
          <Link to="/" className="btn btn-outline-primary">
            <Home size={18} className="me-1" />
            Retour à l'accueil
          </Link>
        </div>
      </div>

      <style>{`
        @media print {
          /* Hide everything except our print content */
          body * {
            visibility: hidden;
          }
          .print-content, .print-content * {
            visibility: visible;
          }
          .print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          
          /* Hide header and footer */
          header, footer, nav, .d-print-none {
            display: none !important;
          }
          
          /* Other print styles */
          .container {
            width: 100%;
            max-width: none;
          }
          
          .card {
            border: none !important;
            break-inside: avoid;
          }
          
          .card-body {
            padding: 0.5rem;
          }
          
          .btn {
            display: none !important;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
        }
      `}</style>
    </Layout>
  );
};

export default WorkshopConfirmationPage;